import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n/config';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);

  // Extract language from URL path
  const getLanguageFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const potentialLang = pathSegments[0];

    if (SUPPORTED_LANGUAGES.includes(potentialLang)) {
      return potentialLang;
    }

    return DEFAULT_LANGUAGE;
  };

  // Initialize language from URL or localStorage
  useEffect(() => {
    const urlLang = getLanguageFromPath();
    const storedLang = localStorage.getItem('preferredLanguage');

    // Priority: URL > localStorage > browser > default
    const initialLang = urlLang || storedLang || navigator.language.split('-')[0] || DEFAULT_LANGUAGE;

    // Ensure it's a supported language
    const finalLang = SUPPORTED_LANGUAGES.includes(initialLang) ? initialLang : DEFAULT_LANGUAGE;

    setCurrentLanguage(finalLang);
    i18n.changeLanguage(finalLang);

    // Store preference
    localStorage.setItem('preferredLanguage', finalLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, location.pathname]);

  // Change language and update URL
  const changeLanguage = (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) {
      console.warn(`Language ${newLang} is not supported`);
      return;
    }

    // Update i18next
    i18n.changeLanguage(newLang);

    // Update state
    setCurrentLanguage(newLang);

    // Store preference
    localStorage.setItem('preferredLanguage', newLang);

    // Update URL
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|pt|es|it|fr)/, '');
    const newPath = `/${newLang}${pathWithoutLang || ''}`;

    navigate(newPath, { replace: true });
  };

  // Get path with language prefix
  const getLocalizedPath = (path) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    getLocalizedPath,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

export default LanguageContext;
